import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import FavoriteButton from '../../../../components/olymp/FavoriteButton';
import ListButton from '../../../../components/olymp/ListButton';
import SensitivityBadge from '../../../../components/olymp/SensitivityBadge';
import FrequencyCell from '../../../../components/olymp/FrequencyCell';
import ExpandedRow from './ExpandedRow';
import { getPrimaryName as getDefaultPrimaryName, getOMOPStatusColor, getOMOPStatusLabel } from '../utils/helpers';

/**
 * Parameter Row Component
 * Renders a single parameter row in the data catalog table
 *
 * @param {Object} param - Parameter data
 * @param {string} nameView - Name view mode ('charite', 'omop', 'both')
 * @param {boolean} isExpanded - Whether row is expanded
 * @param {Function} onRowClick - Handler for row click
 * @param {Function} onFavoriteToggle - Handler for favorite toggle
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} getParameterListStatus - Get list status for parameter
 * @param {Function} isParameterInAnyList - Check if parameter is in any list
 * @param {Function} handleRemoveFromList - Remove parameter from list
 * @param {Function} handleAddToList - Add parameter to list
 * @param {Function} handleOpenListDialog - Open list dialog
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} getPrimaryDescription - Get primary description
 * @param {Object} subtlePulse - Animation for deletion requests
 */
const ParameterRow = ({
  param,
  nameView,
  isExpanded,
  onRowClick,
  onFavoriteToggle,
  activeWorkingList,
  parameterLists,
  getParameterListStatus,
  isParameterInAnyList,
  handleRemoveFromList,
  handleAddToList,
  handleOpenListDialog,
  allTagsWithStats,
  getIconComponent,
  getPrimaryDescription,
  subtlePulse,
}) => {
  // Use imported helper or fallback
  const getPrimaryName = (p) => getDefaultPrimaryName(p, nameView);

  return (
    <React.Fragment key={param.id}>
      <TableRow
        hover
        onClick={() => onRowClick(param.id)}
        sx={{
          cursor: 'pointer',
          '& > *': { borderBottom: 'unset' }
        }}
      >
        {/* Lists Columns - Single list mode */}
        {activeWorkingList.length === 1 && parameterLists && (() => {
          const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
          const inListA = listA && listA.parameterIds.includes(param.id);

          return (
            <TableCell align="center">
              {inListA ? (
                <Tooltip title={`In Liste: ${listA?.name} (Klicken zum Entfernen)`}>
                  <Box
                    onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[0], e)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: '#2196f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#1976d2',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    A
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={`Zu Liste hinzufügen: ${listA?.name}`}>
                  <Box
                    onClick={(e) => handleAddToList(param.id, activeWorkingList[0], e)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '2px solid #2196f3',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2196f3',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                  </Box>
                </Tooltip>
              )}
            </TableCell>
          );
        })()}

        {/* Lists Columns - Comparison mode (2 lists) */}
        {activeWorkingList.length === 2 && parameterLists && (() => {
          const status = getParameterListStatus(param.id);
          const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
          const listB = parameterLists.find(l => l.id === activeWorkingList[1]);

          const inListA = status === 'list1Only' || status === 'both';
          const inListB = status === 'list2Only' || status === 'both';

          return (
            <>
              <TableCell align="center">
                {inListA ? (
                  <Tooltip title={`In Liste A: ${listA?.name} (Klicken zum Entfernen)`}>
                    <Box
                      onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[0], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      A
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={`Zu Liste A hinzufügen: ${listA?.name}`}>
                    <Box
                      onClick={(e) => handleAddToList(param.id, activeWorkingList[0], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px solid #2196f3',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#2196f3',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell align="center">
                {inListB ? (
                  <Tooltip title={`In Liste B: ${listB?.name} (Klicken zum Entfernen)`}>
                    <Box
                      onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[1], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#388e3c',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      B
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={`Zu Liste B hinzufügen: ${listB?.name}`}>
                    <Box
                      onClick={(e) => handleAddToList(param.id, activeWorkingList[1], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px solid #4caf50',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#4caf50',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
            </>
          );
        })()}

        {/* Expand Button Column */}
        <TableCell>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(param.id);
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Parameter Name Column */}
        <TableCell>
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" display="block">
                  <strong>Lokal:</strong> {param.name}
                </Typography>
                {param.omopName && (
                  <Typography variant="caption" display="block">
                    <strong>OMOP:</strong> {param.omopName}
                  </Typography>
                )}
                {param.omopCode && (
                  <Typography variant="caption" display="block">
                    <strong>Code:</strong> {param.omopCode}
                  </Typography>
                )}
                {param.omopVocabulary && (
                  <Typography variant="caption" display="block">
                    <strong>Vocabulary:</strong> {param.omopVocabulary}
                  </Typography>
                )}
              </Box>
            }
            placement="right"
            arrow
          >
            <Box>
              {nameView === 'both' ? (
                <>
                  {/* Lokaler Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ minWidth: 60 }}>
                      🏥 Lokal:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {param.name}
                    </Typography>
                  </Box>
                  {/* OMOP Name */}
                  {param.omopName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="caption" color="primary" fontWeight="bold" sx={{ minWidth: 60 }}>
                        🔷 OMOP:
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {param.omopName}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" fontWeight="bold">
                  {getPrimaryName(param)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="textSecondary">
                  {getPrimaryDescription(param)}
                </Typography>
                {/* Indicator for multiple units */}
                {param.units && param.units.length > 1 && (
                  <Tooltip title={`Mehrere Einheiten: ${param.units.map(u => u.unit).join(', ')}`}>
                    <Chip
                      label={`${param.units.length} Einheiten`}
                      size="small"
                      color="warning"
                      sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Tooltip>
        </TableCell>

        {/* Tags Column */}
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {param.tags.map(tag => {
              const tagData = allTagsWithStats.find(t => t.name === tag);
              const isPrivate = tagData?.isPrivate || false;
              const CustomIconComponent = tagData?.icon ? getIconComponent(tagData.icon) : null;

              return (
                <Tooltip
                  key={tag}
                  title={tagData?.createdBy ? `Erstellt von: ${tagData.createdBy}` : ''}
                >
                  <Chip
                    icon={
                      CustomIconComponent ? (
                        <CustomIconComponent fontSize="small" />
                      ) : isPrivate ? (
                        <LockIcon fontSize="small" />
                      ) : (
                        <PublicIcon fontSize="small" />
                      )
                    }
                    label={tag}
                    size="small"
                    color={isPrivate ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                </Tooltip>
              );
            })}
            {/* IT-System Badge */}
            {param.sourceSystem && (
              <Chip
                icon={<StorageIcon />}
                label={param.sourceSystem}
                size="small"
                color="info"
                variant="filled"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </TableCell>

        {/* Frequency Column */}
        <TableCell>
          <FrequencyCell
            caseCoverage={param.frequencyMetrics?.caseCoverage || param.frequency || 0}
            stationCount={param.frequencyMetrics?.stationCount || param.stations?.length || 0}
          />
        </TableCell>

        {/* OMOP Status Column (conditional) */}
        {nameView !== 'charite' && (
          <TableCell>
            <Chip
              label={getOMOPStatusLabel(param.omopStatus)}
              size="small"
              color={getOMOPStatusColor(param.omopStatus)}
            />
            {param.omopCode && (
              <Typography variant="caption" display="block" color="textSecondary">
                {param.omopCode}
              </Typography>
            )}
          </TableCell>
        )}

        {/* Sensitivity Column */}
        <TableCell>
          <SensitivityBadge level={param.sensitivity} />
        </TableCell>

        {/* Actions Column */}
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <FavoriteButton
              isFavorite={param.isFavorite}
              onClick={() => onFavoriteToggle(param.id)}
            />
            <ListButton
              isInList={isParameterInAnyList(param.id)}
              onClick={(e) => handleOpenListDialog(param.id, e)}
              activeListName={activeWorkingList.length === 1 && parameterLists ? parameterLists.find(l => l.id === activeWorkingList[0])?.name : null}
            />
            <IconButton
              size="small"
              component={Link}
              to={`/parameter/${param.id}`}
              onClick={(e) => e.stopPropagation()}
              title="Alle Details anzeigen"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable Row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={nameView !== 'charite' ? (activeWorkingList.length > 0 ? 8 : 7) : (activeWorkingList.length > 0 ? 7 : 6)}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandedRow
              param={param}
              parameterLists={parameterLists}
              activeWorkingList={activeWorkingList}
              handleOpenListDialog={handleOpenListDialog}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default ParameterRow;
